import {
  CandlestickSeries,
  createChart,
  createSeriesMarkers,
  HistogramSeries,
  LineSeries,
  Time,
} from 'lightweight-charts';
import React, { useEffect, useRef, useState } from 'react';

interface Candle {
  time: number; // 분봉(초 단위)
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

interface MA {
  time: number;
  value: number;
}

interface TradingMarker {
  time: number;
  type: 'buy' | 'sell';
  price: number;
  quantity: number;
  profit?: number;
  note?: string;
}

interface Props {
  data: Candle[];
  volumeData?: { time: number; value: number }[];
  ma5?: MA[];
  ma10?: MA[];
  ma20?: MA[];
  ma60?: MA[];
  ma120?: MA[];
  theme?: 'dark' | 'light';
  height?: number;
  showOHLCInfo?: boolean;
  onToggleOHLCInfo?: (show: boolean) => void;
  tradingMarkers?: TradingMarker[];
}

const StockChart: React.FC<Props> = ({
  data,
  volumeData,
  ma5,
  ma10,
  ma20,
  ma60,
  ma120,
  theme = 'dark',
  height,
  showOHLCInfo = true,
  onToggleOHLCInfo,
  tradingMarkers,
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const candleSeriesRef = useRef<any>(null);
  const markersApiRef = useRef<any>(null); // lightweight-charts 5.x 마커 API
  const ma5SeriesRef = useRef<any>(null);
  const ma10SeriesRef = useRef<any>(null);
  const ma20SeriesRef = useRef<any>(null);
  const ma60SeriesRef = useRef<any>(null);
  const ma120SeriesRef = useRef<any>(null);
  const volumeSeriesRef = useRef<any>(null);
  const [hoverInfo, setHoverInfo] = useState<Candle | null>(null);
  const [hoveredMarker, setHoveredMarker] = useState<TradingMarker | null>(
    null
  );
  const [markerTooltipPosition, setMarkerTooltipPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const isAutoScrollRef = useRef<boolean>(true);

  // MA 색상 정의 (차트와 info UI 모두 동일하게)
  const maColors = [
    { label: 'MA5', color: theme === 'dark' ? '#00bfff' : '#ff6b35' }, // 하늘색/주황색
    { label: 'MA10', color: theme === 'dark' ? '#d32f2f' : '#e53a3a' }, // 빨강
    { label: 'MA20', color: theme === 'dark' ? '#388e3c' : '#1763b6' }, // 초록/파랑
    { label: 'MA60', color: theme === 'dark' ? '#8e24aa' : '#f7b500' }, // 보라/노랑
    { label: 'MA120', color: theme === 'dark' ? '#757575' : '#495057' }, // 회색
  ];

  // 드래그 줌 상태 관리
  const [dragZoom, setDragZoom] = useState<{
    start: number | null;
    end: number | null;
    xStart: number | null;
    xEnd: number | null;
    active: boolean;
    valid: boolean;
  }>({
    start: null,
    end: null,
    xStart: null,
    xEnd: null,
    active: false,
    valid: false,
  });

  // pointer down: 시작 logical index, x좌표 저장
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!chartRef.current) return;
    const bounding = chartContainerRef.current?.getBoundingClientRect();
    if (!bounding) return;
    const x = e.clientX - bounding.left;
    const logical = chartRef.current.timeScale().coordinateToLogical(x);
    setDragZoom({
      start: logical,
      end: null,
      xStart: x,
      xEnd: null,
      active: true,
      valid: true,
    });
  };

  // pointer move: 드래그 영역 시각화, 차트 밖이면 취소
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragZoom.active || dragZoom.start === null || dragZoom.xStart === null)
      return;
    if (!chartRef.current) return;
    const bounding = chartContainerRef.current?.getBoundingClientRect();
    if (!bounding) return;
    const x = e.clientX - bounding.left;
    if (x < 0 || x > bounding.width) {
      setDragZoom(z => ({ ...z, valid: false }));
      return;
    }
    const logical = chartRef.current.timeScale().coordinateToLogical(x);
    setDragZoom(z => ({ ...z, end: logical, xEnd: x, valid: true }));
  };

  // pointer up: 확대/축소 적용
  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (
      !chartRef.current ||
      !dragZoom.active ||
      !dragZoom.valid ||
      dragZoom.start === null ||
      dragZoom.end === null
    ) {
      setDragZoom({
        start: null,
        end: null,
        xStart: null,
        xEnd: null,
        active: false,
        valid: false,
      });
      return;
    }

    const timeScale = chartRef.current.timeScale();
    // 논리 인덱스 → 정수, 안전하게 보정
    const start = Math.max(
      0,
      Math.floor(Math.min(dragZoom.start, dragZoom.end))
    );
    const end = Math.min(
      data.length - 1,
      Math.ceil(Math.max(dragZoom.start, dragZoom.end))
    );

    const firstPoint = data[start];
    const lastPoint = data[end];

    if (!firstPoint || !lastPoint) {
      setDragZoom({
        start: null,
        end: null,
        xStart: null,
        xEnd: null,
        active: false,
        valid: false,
      });
      return;
    }

    // 드래그 방향 판별
    const isRight = dragZoom.end > dragZoom.start;

    isAutoScrollRef.current = false;

    if (isRight) {
      // 우측(최신 쪽) 드래그: visible range 지정
      timeScale.setVisibleRange({
        from: firstPoint.time as Time,
        to: lastPoint.time as Time,
      });
    } else {
      // 좌측(과거 쪽) 드래그: 전체 보기
      chartRef.current.timeScale().fitContent();
    }

    setDragZoom({
      start: null,
      end: null,
      xStart: null,
      xEnd: null,
      active: false,
      valid: false,
    });
  };

  // 드래그 영역 시각화 계산
  const getDragZoomVisual = () => {
    if (
      !dragZoom.active ||
      dragZoom.start === null ||
      dragZoom.end === null ||
      !chartRef.current ||
      !dragZoom.valid
    )
      return null;
    const startX = chartRef.current
      .timeScale()
      .logicalToCoordinate(dragZoom.start);
    const endX = chartRef.current.timeScale().logicalToCoordinate(dragZoom.end);
    if (startX === null || endX === null) return null;
    const left = Math.min(startX, endX);
    const width = Math.abs(endX - startX);
    const isZoomIn =
      dragZoom.xEnd !== null && dragZoom.xStart !== null
        ? dragZoom.xEnd > dragZoom.xStart
        : dragZoom.end > dragZoom.start;
    return { left, width, isZoomIn, startX, endX };
  };

  // 마우스 휠로 좌우 이동(패닝)
  const handleWheel = (e: React.WheelEvent<HTMLDivElement> | WheelEvent) => {
    isAutoScrollRef.current = false;
    if (e.deltaY !== 0 && chartRef.current) {
      // 데이터가 너무 적으면 logical range 계산이 깨질 수 있어 휠 패닝 비활성화
      if (data.length < 2) {
        return;
      }
      const timeScale = chartRef.current.timeScale();
      const range = timeScale.getVisibleLogicalRange();
      if (range) {
        const shift = e.deltaY > 0 ? 5 : -5;
        // range가 역전된 상태로 올 수 있어 정렬
        const safeFrom = Math.min(range.from, range.to);
        const safeTo = Math.max(range.from, range.to);
        const rangeWidth = safeTo - safeFrom;

        // 데이터 범위 제한
        const maxLogical = data.length - 1;
        if (maxLogical < 1) {
          return;
        }
        let from = safeFrom + shift;
        let to = safeTo + shift;

        // 왼쪽 경계 체크 (0보다 작아지지 않도록)
        if (from < 0) {
          from = 0;
          to = Math.min(rangeWidth, maxLogical);
        }

        // 오른쪽 경계 체크 (데이터 끝을 넘어가지 않도록)
        if (to > maxLogical) {
          to = maxLogical;
          from = Math.max(0, maxLogical - rangeWidth);
        }

        // 최소 범위 보장 (최소 1개 캔들)
        if (from > to) {
          const tmp = from;
          from = to;
          to = tmp;
        }
        if (from === to) {
          if (from < maxLogical) {
            to = from + 1;
          } else if (from > 0) {
            from = to - 1;
          }
        }

        // 최종 방어: from/to 정렬 + clamp
        const finalFrom = Math.max(0, Math.min(from, to));
        const finalTo = Math.min(maxLogical, Math.max(from, to));
        if (finalFrom <= finalTo) {
          timeScale.setVisibleLogicalRange({ from: finalFrom, to: finalTo });
        }
      }
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  };

  // 차트 생성 및 시리즈 추가
  // 데이터가 잦게 변동하므로 차트를 매번 재생성하지 않기 위해 테마 변경에만 반응합니다.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    console.log('차트 생성 useEffect 실행', {
      containerExists: !!chartContainerRef.current,
      dataLength: data.length,
      theme,
      chartExists: !!chartRef.current,
    });

    if (!chartContainerRef.current || data.length === 0) return;

    const container = chartContainerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // 컨테이너 크기가 유효한지 확인
    if (containerWidth <= 0 || containerHeight <= 0) {
      return;
    }

    // barSpacing 기본값 설정
    const minBarSpacing =
      data.length > 0 ? Math.floor((containerWidth - 40) / data.length) : 5;
    const barSpacing = Math.max(minBarSpacing, 5);

    const hasVolume = Boolean(volumeData && volumeData.length > 0);

    const chart = createChart(container, {
      width: containerWidth,
      height: containerHeight,
      layout: {
        background: { color: theme === 'dark' ? '#181c24' : '#fff' },
        textColor: theme === 'dark' ? '#d1d4dc' : '#222',
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: theme === 'dark' ? '#222' : '#e9ecef' },
        horzLines: { color: theme === 'dark' ? '#222' : '#e9ecef' },
      },
      crosshair: { mode: 1 },
      rightPriceScale: {
        borderColor: theme === 'dark' ? '#444' : '#dee2e6',
        scaleMargins: {
          top: 0.1,
          bottom: 0.2,
        },
        autoScale: true, // 자동 스케일링 활성화
      },
      leftPriceScale: hasVolume
        ? {
            visible: true,
            borderColor: theme === 'dark' ? '#444' : '#dee2e6',
            scaleMargins: {
              top: 0.8,
              bottom: 0.02,
            },
            autoScale: true,
          }
        : { visible: false },
      timeScale: {
        visible: false, // 시간 축 자체를 완전히 숨김
        borderColor: theme === 'dark' ? '#444' : '#dee2e6',
        timeVisible: false,
        secondsVisible: false,
        fixLeftEdge: true,
        fixRightEdge: true,
        minBarSpacing: 2,
        maxBarSpacing: 50,
        rightOffset: 0,
        lockVisibleTimeRangeOnResize: true,
        barSpacing,
      },
      handleScale: {
        axisPressedMouseMove: {
          time: false,
          price: false,
        },
        mouseWheel: false,
        pinch: false,
      },
    });

    chartRef.current = chart;

    // 캔들 시리즈
    const uniqueSortedData = Array.from(
      new Map(data.map(d => [d.time, d])).values()
    ).sort((a, b) => a.time - b.time);

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: theme === 'dark' ? '#00ff88' : '#ff4757',
      downColor: theme === 'dark' ? '#ff4757' : '#1763b6',
      borderUpColor: theme === 'dark' ? '#00ff88' : '#ff4757',
      borderDownColor: theme === 'dark' ? '#ff4757' : '#1763b6',
      wickUpColor: theme === 'dark' ? '#00ff88' : '#ff4757',
      wickDownColor: theme === 'dark' ? '#ff4757' : '#1763b6',
      priceFormat: { type: 'price', precision: 0, minMove: 1 },
    });

    candleSeriesRef.current = candleSeries;
    candleSeries.setData(
      uniqueSortedData.map(d => ({
        time: d.time as Time,
        open: d.open,
        high: d.high,
        low: d.low,
        close: d.close,
      }))
    );

    // lightweight-charts 5.x: createSeriesMarkers로 마커 API 인스턴스 생성
    try {
      // 초기 마커 데이터 설정 (tradingMarkers가 있으면 바로 추가)
      const initialMarkers =
        tradingMarkers && tradingMarkers.length > 0
          ? tradingMarkers.map(
              marker =>
                ({
                  time: marker.time as Time,
                  position: (marker.type === 'buy'
                    ? 'belowBar'
                    : 'aboveBar') as 'belowBar' | 'aboveBar',
                  color: marker.type === 'buy' ? '#26a69a' : '#ef5350',
                  shape: (marker.type === 'buy' ? 'arrowUp' : 'arrowDown') as
                    | 'arrowUp'
                    | 'arrowDown',
                  text: `${marker.type === 'buy' ? '매수' : '매도'} ${
                    marker.quantity
                  }주${
                    marker.profit !== undefined
                      ? ` (${
                          marker.profit > 0 ? '+' : ''
                        }${marker.profit.toFixed(1)}%)`
                      : ''
                  }`,
                  size: 1,
                } as any)
            )
          : [];

      markersApiRef.current = createSeriesMarkers(candleSeries, initialMarkers);
      console.log(
        '✅ 마커 API 인스턴스 생성 완료 (초기 마커 포함):',
        initialMarkers.length
      );
    } catch (error) {
      console.error('❌ 마커 API 인스턴스 생성 실패:', error);
    }

    // 구분선 제거 (사용자 요청)
    // const separatorValue = Math.min(...uniqueSortedData.map(d => d.low)) * 0.99; // 최저가보다 약간 아래에 선을 그림
    // candleSeries.createPriceLine({
    //   price: separatorValue,
    //   color: theme === 'dark' ? '#444' : '#dee2e6',
    //   lineWidth: 2,
    //   lineStyle: 0, // 0 = 실선
    //   axisLabelVisible: false,
    //   title: '',
    // });

    // MA 시리즈
    const addMASeries = (
      ma: MA[] | undefined,
      color: string,
      ref: React.MutableRefObject<any>
    ) => {
      const series = chart.addSeries(LineSeries, {
        color,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
      });
      ref.current = series;
      // MA 데이터도 시간 순으로 정렬
      const sortedMa = (ma ?? []).sort((a, b) => a.time - b.time);
      series.setData(
        sortedMa.map(m => ({ time: m.time as Time, value: m.value }))
      );
      return series;
    };

    addMASeries(ma5, theme === 'dark' ? '#00bfff' : '#ff6b35', ma5SeriesRef);
    addMASeries(ma10, theme === 'dark' ? '#d32f2f' : '#e53a3a', ma10SeriesRef);
    addMASeries(ma20, theme === 'dark' ? '#388e3c' : '#1763b6', ma20SeriesRef);
    addMASeries(ma60, theme === 'dark' ? '#8e24aa' : '#f7b500', ma60SeriesRef);
    addMASeries(
      ma120,
      theme === 'dark' ? '#757575' : '#495057',
      ma120SeriesRef
    );

    // 이동평균선 추가 후 마커를 다시 설정하여 위에 표시되도록 함
    if (markersApiRef.current && tradingMarkers && tradingMarkers.length > 0) {
      const markers = tradingMarkers.map(
        marker =>
          ({
            time: marker.time as Time,
            position: (marker.type === 'buy' ? 'belowBar' : 'aboveBar') as
              | 'belowBar'
              | 'aboveBar',
            color: marker.type === 'buy' ? '#26a69a' : '#ef5350',
            shape: (marker.type === 'buy' ? 'arrowUp' : 'arrowDown') as
              | 'arrowUp'
              | 'arrowDown',
            text: `${marker.type === 'buy' ? '매수' : '매도'} ${
              marker.quantity
            }주${
              marker.profit !== undefined
                ? ` (${marker.profit > 0 ? '+' : ''}${marker.profit.toFixed(
                    1
                  )}%)`
                : ''
            }`,
            size: 1,
          } as any)
      );
      try {
        markersApiRef.current.setMarkers(markers);
        console.log(
          '✅ 마커를 이동평균선 위에 표시하도록 재설정:',
          markers.length,
          '개'
        );
      } catch (error) {
        console.error('❌ 마커 재설정 중 오류:', error);
      }
    }

    // 캔들차트의 priceScale 설정 업데이트 (오른쪽)
    chart.priceScale('right').applyOptions({
      scaleMargins: {
        top: 0.02, // 상단 2% 여백
        bottom: hasVolume ? 0.2 : 0.02, // 거래량 영역 확보
      },
      borderColor: theme === 'dark' ? '#444' : '#dee2e6',
      visible: true,
      alignLabels: true,
      autoScale: true,
    });

    // 거래량 차트 (왼쪽 priceScale 사용)
    if (volumeData) {
      const volumeSeries = chart.addSeries(HistogramSeries, {
        color: theme === 'dark' ? '#8884d8' : '#6c757d',
        priceFormat: { type: 'volume' },
        priceScaleId: 'left',
      });

      volumeSeriesRef.current = volumeSeries;
      // 거래량 데이터도 시간 순으로 정렬
      const sortedVolumeData = [...volumeData].sort((a, b) => a.time - b.time);
      volumeSeries.setData(
        sortedVolumeData.map(v => ({ time: v.time as Time, value: v.value }))
      );

      // 거래량 차트의 priceScale 설정 (왼쪽)
      chart.priceScale('left').applyOptions({
        scaleMargins: {
          top: 0.8, // 상단 80%는 캔들차트 영역
          bottom: 0.02, // 하단 18% 사용 (2% 여백)
        },
        borderColor: theme === 'dark' ? '#444' : '#dee2e6',
        textColor: theme === 'dark' ? '#d1d4dc' : '#495057',
        visible: true,
        alignLabels: true,
        autoScale: true,
      });

      // 구분선 제거 (사용자 요청)
      // candleSeries.createPriceLine({
      //   price: Math.min(...data.map(d => d.low)) * 0.98, // 최저가보다 약간 아래에 선을 그림
      //   color: theme === 'dark' ? '#444' : '#dee2e6',
      //   lineWidth: 2,
      //   lineStyle: 0,
      //   axisLabelVisible: false,
      //   title: '',
      // });
    }

    // 초기 로드 시 우측 끝에 포커스
    if (data.length > 0) {
      const timeScale = chart.timeScale();
      const newRange = {
        from: Math.max(0, data.length - 50), // 최근 50개 캔들 표시
        to: data.length - 1,
      };
      timeScale.setVisibleLogicalRange(newRange);
    }

    chart.subscribeCrosshairMove(param => {
      if (param.time) {
        const t =
          typeof param.time === 'number' ? param.time : Number(param.time);
        // 시간 값으로 정확히 일치하는 캔들 찾기
        let candle = data.find(d => d.time === t);

        // 정확히 일치하지 않으면 가장 가까운 캔들 찾기 (1초 오차 허용)
        if (!candle) {
          candle = data.find(d => Math.abs(d.time - t) <= 1);
        }

        // 그래도 없으면 파라미터에서 직접 데이터 가져오기
        if (!candle && param.seriesData) {
          const seriesData = param.seriesData.get(candleSeriesRef.current);
          if (seriesData) {
            // lightweight-charts에서 제공하는 데이터 형식 변환
            candle = {
              time: t,
              open: (seriesData as any).open,
              high: (seriesData as any).high,
              low: (seriesData as any).low,
              close: (seriesData as any).close,
              volume: (seriesData as any).volume,
            };
          }
        }

        setHoverInfo(candle || null);

        // 마커 호버 감지 (tradingMarkers가 있고 crosshair가 마커 근처에 있을 때)
        if (tradingMarkers && tradingMarkers.length > 0 && param.point) {
          // 같은 캔들 시간에 있는 모든 마커 찾기 (시간 범위를 더 넓게)
          const nearbyMarkers = tradingMarkers.filter(marker => {
            // 같은 캔들 시간이거나 매우 가까운 시간 (5분 이내로 확대)
            const timeDiff = Math.abs(marker.time - t);
            return timeDiff < 300000; // 5분 이내 (매도 마커 감지 개선)
          });

          if (nearbyMarkers.length > 0) {
            // 같은 봉에 여러 마커가 있으면 커서 위치에 따라 선택
            let selectedMarker: TradingMarker | null = null;

            if (nearbyMarkers.length > 1) {
              // 차트 컨테이너의 중간 높이 계산
              const container = chartContainerRef.current;
              if (container) {
                const containerHeight = container.clientHeight;
                const middleY = containerHeight / 2;
                const cursorY = param.point.y;

                // 커서가 차트 중간보다 위에 있으면 매도, 아래면 매수
                if (cursorY < middleY) {
                  // 위쪽: 매도 우선
                  const sellMarker = nearbyMarkers.find(m => m.type === 'sell');
                  selectedMarker = sellMarker || nearbyMarkers[0];
                } else {
                  // 아래쪽: 매수 우선
                  const buyMarker = nearbyMarkers.find(m => m.type === 'buy');
                  selectedMarker = buyMarker || nearbyMarkers[0];
                }
              } else {
                // 컨테이너 정보가 없으면 매도 우선
                const sellMarker = nearbyMarkers.find(m => m.type === 'sell');
                selectedMarker = sellMarker || nearbyMarkers[0];
              }
            } else {
              // 마커가 하나만 있으면 그대로 사용 (매수든 매도든)
              selectedMarker = nearbyMarkers[0];
            }

            // selectedMarker가 null이 아니면 툴팁 표시
            if (selectedMarker) {
              setHoveredMarker(selectedMarker);
              setMarkerTooltipPosition({ x: param.point.x, y: param.point.y });
            } else {
              setHoveredMarker(null);
              setMarkerTooltipPosition(null);
            }
          } else {
            setHoveredMarker(null);
            setMarkerTooltipPosition(null);
          }
        } else {
          setHoveredMarker(null);
          setMarkerTooltipPosition(null);
        }
      } else {
        setHoverInfo(null);
        setHoveredMarker(null);
        setMarkerTooltipPosition(null);
      }
    });

    // ResizeObserver 설정
    const resizeObserver = new ResizeObserver(entries => {
      window.requestAnimationFrame(() => {
        if (!chartRef.current || !entries[0]) return;

        const { width, height } = entries[0].contentRect;

        // 유효한 크기인지 확인
        if (width <= 0 || height <= 0) return;

        console.log('ResizeObserver fired:', {
          width,
          height,
          currentBarSpacing: chartRef.current.timeScale().options().barSpacing,
          time: new Date().toISOString(),
        });

        // 리사이즈 전 현재 상태 저장
        const currentBarSpacing = chartRef.current
          .timeScale()
          .options().barSpacing;
        const currentVisibleRange = chartRef.current
          .timeScale()
          .getVisibleLogicalRange();

        // 차트 크기 업데이트
        chartRef.current.applyOptions({
          width: Math.floor(width),
          height: Math.floor(height),
        });

        // 약간의 지연 후 상태 복원
        setTimeout(() => {
          if (!chartRef.current) return;

          // 리사이즈 후 상태 복원
          if (currentVisibleRange) {
            chartRef.current
              .timeScale()
              .setVisibleLogicalRange(currentVisibleRange);
          }
          if (currentBarSpacing) {
            chartRef.current.timeScale().applyOptions({
              barSpacing: currentBarSpacing,
            });
          }

          console.log('ResizeObserver after state restore:', {
            barSpacing: chartRef.current.timeScale().options().barSpacing,
            time: new Date().toISOString(),
          });
        }, 0);
      });
    });

    if (container) {
      try {
        resizeObserver.observe(container);
      } catch (error) {
        console.error('ResizeObserver error:', error);
      }
    }

    // cleanup 함수
    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
      resizeObserver.disconnect();

      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [theme]);

  // 데이터 업데이트 시 차트 업데이트
  useEffect(() => {
    if (!chartRef.current || !candleSeriesRef.current) return;

    const uniqueSortedData = Array.from(
      new Map(data.map(d => [d.time, d])).values()
    ).sort((a, b) => a.time - b.time);

    candleSeriesRef.current.setData(
      uniqueSortedData.map(d => ({
        time: d.time as Time,
        open: d.open,
        high: d.high,
        low: d.low,
        close: d.close,
      }))
    );

    // 거래량 데이터가 있으면 왼쪽 눈금 활성화 및 데이터 설정
    if (volumeData && volumeData.length > 0 && volumeSeriesRef.current) {
      // 왼쪽 눈금 활성화
      chartRef.current.priceScale('left').applyOptions({
        visible: true,
        scaleMargins: {
          top: 0.8,
          bottom: 0.02,
        },
        borderColor: theme === 'dark' ? '#444' : '#dee2e6',
        textColor: theme === 'dark' ? '#d1d4dc' : '#495057',
      });

      // 우측 눈금 영역 조정 (거래량 영역 확보)
      chartRef.current.priceScale('right').applyOptions({
        scaleMargins: {
          top: 0.02,
          bottom: 0.2,
        },
      });
    }

    // 새로운 데이터가 추가된 경우에만 자동 스크롤
    if (isAutoScrollRef.current && data.length > 0) {
      const timeScale = chartRef.current.timeScale();
      const newRange = {
        from: Math.max(0, data.length - 50), // 최근 50개 캔들 표시
        to: data.length - 1,
      };
      timeScale.setVisibleLogicalRange(newRange);
    }
  }, [data, volumeData, theme]); // theme 변경 시에도 눈금 색상 업데이트를 위해 의존성 추가

  // 데이터가 변경될 때마다 각 시리즈에 setData 호출
  useEffect(() => {
    if (candleSeriesRef.current && data.length > 0) {
      // 유효한 데이터 필터링 및 중복 제거, 시간 순 정렬
      const filtered = data.filter(
        d =>
          typeof d.time === 'number' &&
          !isNaN(d.time) &&
          d.time !== null &&
          d.time !== undefined
      );

      // 중복 제거 (동일한 time 값 중 마지막 것만 유지)
      const uniqueData = Array.from(
        new Map(filtered.map(d => [d.time, d])).values()
      );

      // 시간 순으로 정렬 (ascending order - lightweight-charts 요구사항)
      const uniqueSortedData = uniqueData.sort((a, b) => a.time - b.time);

      console.log('차트 데이터 업데이트:', {
        원본데이터개수: data.length,
        필터후개수: filtered.length,
        중복제거후개수: uniqueData.length,
        정렬후개수: uniqueSortedData.length,
        첫번째시간: uniqueSortedData[0]?.time,
        마지막시간: uniqueSortedData[uniqueSortedData.length - 1]?.time,
      });

      candleSeriesRef.current.setData(
        uniqueSortedData.map(d => ({
          time: d.time as Time,
          open: d.open,
          high: d.high,
          low: d.low,
          close: d.close,
        }))
      );
    }

    // MA 데이터도 시간 순으로 정렬
    if (ma5SeriesRef.current && ma5 && ma5.length > 0) {
      const sortedMa5 = [...ma5].sort((a, b) => a.time - b.time);
      ma5SeriesRef.current.setData(
        sortedMa5.map(m => ({ time: m.time, value: m.value }))
      );
    }
    if (ma10SeriesRef.current && ma10 && ma10.length > 0) {
      const sortedMa10 = [...ma10].sort((a, b) => a.time - b.time);
      ma10SeriesRef.current.setData(
        sortedMa10.map(m => ({ time: m.time, value: m.value }))
      );
    }
    if (ma20SeriesRef.current && ma20 && ma20.length > 0) {
      const sortedMa20 = [...ma20].sort((a, b) => a.time - b.time);
      ma20SeriesRef.current.setData(
        sortedMa20.map(m => ({ time: m.time, value: m.value }))
      );
    }
    if (ma60SeriesRef.current && ma60 && ma60.length > 0) {
      const sortedMa60 = [...ma60].sort((a, b) => a.time - b.time);
      ma60SeriesRef.current.setData(
        sortedMa60.map(m => ({ time: m.time, value: m.value }))
      );
    }
    if (ma120SeriesRef.current && ma120 && ma120.length > 0) {
      const sortedMa120 = [...ma120].sort((a, b) => a.time - b.time);
      ma120SeriesRef.current.setData(
        sortedMa120.map(m => ({ time: m.time, value: m.value }))
      );
    }
    if (volumeSeriesRef.current && volumeData && volumeData.length > 0) {
      const sortedVolumeData = [...volumeData].sort((a, b) => a.time - b.time);
      volumeSeriesRef.current.setData(
        sortedVolumeData.map(v => ({ time: v.time, value: v.value }))
      );
    }
  }, [data, ma5, ma10, ma20, ma60, ma120, volumeData]);

  // 트레이딩 마커 업데이트 (lightweight-charts 5.x 방식: createSeriesMarkers 사용)
  useEffect(() => {
    console.log('🎯 TradingMarkers useEffect 실행:', {
      hasCandleSeries: !!candleSeriesRef.current,
      hasMarkersApi: !!markersApiRef.current,
      markersCount: tradingMarkers?.length || 0,
      markers: tradingMarkers?.slice(0, 2), // 처음 2개만 로그
    });

    if (!markersApiRef.current) {
      console.warn(
        '⚠️ markersApiRef가 없습니다. 차트가 아직 생성되지 않았거나 마커 API가 초기화되지 않았습니다.'
      );
      return;
    }

    const markersApi = markersApiRef.current;

    // 마커가 없으면 기존 마커 제거
    if (!tradingMarkers || tradingMarkers.length === 0) {
      console.log('🧹 마커가 없어서 기존 마커 제거');
      try {
        markersApi.setMarkers([]);
      } catch (error) {
        console.error('❌ 마커 제거 중 오류 발생:', error);
      }
      return;
    }

    // 새 마커 생성
    const markers = tradingMarkers
      .map((marker, index) => {
        try {
          const markerObj = {
            time: marker.time as Time,
            position: (marker.type === 'buy' ? 'belowBar' : 'aboveBar') as
              | 'belowBar'
              | 'aboveBar',
            color: marker.type === 'buy' ? '#26a69a' : '#ef5350', // 매수: 초록, 매도: 빨강
            shape: (marker.type === 'buy' ? 'arrowUp' : 'arrowDown') as
              | 'arrowUp'
              | 'arrowDown', // 화살표 모양
            text: `${marker.type === 'buy' ? '매수' : '매도'} ${
              marker.quantity
            }주${
              marker.profit !== undefined
                ? ` (${marker.profit > 0 ? '+' : ''}${marker.profit.toFixed(
                    1
                  )}%)`
                : ''
            }`,
            size: 1,
          } as any;
          if (index < 2) {
            console.log(`📌 마커 ${index + 1}:`, markerObj);
          }
          return markerObj;
        } catch (error) {
          console.error('❌ 마커 생성 중 오류:', error, marker);
          return null;
        }
      })
      .filter((m): m is NonNullable<typeof m> => m !== null);

    // 마커 적용 (lightweight-charts 5.x 방식: markersApi.setMarkers 사용)
    try {
      console.log('✅ 마커 설정 시도:', markers.length, '개');
      markersApi.setMarkers(markers);
      console.log('✅ 마커 설정 완료!');
    } catch (error) {
      console.error('❌ 마커 설정 중 오류 발생:', error);
    }
  }, [tradingMarkers]);

  // 차트 컨테이너에서 휠 이벤트로 페이지 스크롤 방지
  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return;

    const wheelHandler = (e: WheelEvent) => {
      // 차트 영역에서만 preventDefault 실행
      handleWheel(e);
    };

    // passive: false로 설정하여 preventDefault가 작동하도록 함
    container.addEventListener('wheel', wheelHandler, { passive: false });

    return () => {
      container.removeEventListener('wheel', wheelHandler);
    };
  }, [data.length]);

  // 상단 정보 UI (hoverInfo가 있으면 그걸, 없으면 최신 데이터)
  const renderTopInfo = () => {
    const infoData =
      hoverInfo || (data.length > 0 ? data[data.length - 1] : null);
    if (!infoData) return null;
    const idx = data.findIndex(d => d.time === infoData.time);
    const prevClose = idx > 0 ? data[idx - 1]?.close : undefined;
    const percent = (value?: number) =>
      prevClose !== undefined && value !== undefined && prevClose !== 0
        ? (((value - prevClose) / prevClose) * 100).toFixed(2)
        : undefined;
    const percentColor = (p?: string) => {
      if (!p) return theme === 'dark' ? '#d1d4dc' : '#222';
      return Number(p) > 0
        ? theme === 'dark'
          ? '#00ff88'
          : '#ff4757'
        : theme === 'dark'
        ? '#ff4757'
        : '#1763b6';
    };
    const percentSign = (p?: string) => (p && Number(p) > 0 ? '+' : '');
    const volume = infoData.volume;

    const formatPrice = (price: number) =>
      `₩${Math.floor(price).toLocaleString()}`;

    return (
      <div
        ref={infoRef}
        style={{
          width: '100%',
          background:
            theme === 'dark'
              ? 'rgba(24, 28, 36, 0.3)'
              : 'rgba(255, 255, 255, 0.3)',
          backdropFilter: 'blur(2px)',
          border: `1px solid ${
            theme === 'dark'
              ? 'rgba(68, 68, 68, 0.5)'
              : 'rgba(222, 226, 230, 0.5)'
          }`,
          color: theme === 'dark' ? '#fff' : '#333',
          fontSize: 13,
          padding: '8px 12px 8px 12px',
          boxSizing: 'border-box',
          display: 'block',
          borderRadius: '6px',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px',
            marginBottom: 4,
          }}
        >
          <div style={{ color: theme === 'dark' ? '#d1d4dc' : '#666' }}>
            시가:{' '}
            <span style={{ color: theme === 'dark' ? '#d1d4dc' : '#222' }}>
              {formatPrice(infoData.open)}
            </span>
            {percent(infoData.open) && (
              <span
                style={{
                  color: percentColor(percent(infoData.open)),
                  marginLeft: 4,
                }}
              >
                {percentSign(percent(infoData.open))}
                {percent(infoData.open)}%
              </span>
            )}
          </div>
          <div style={{ color: theme === 'dark' ? '#d1d4dc' : '#666' }}>
            고가:{' '}
            <span style={{ color: theme === 'dark' ? '#d1d4dc' : '#222' }}>
              {formatPrice(infoData.high)}
            </span>
            {percent(infoData.high) && (
              <span
                style={{
                  color: percentColor(percent(infoData.high)),
                  marginLeft: 4,
                }}
              >
                {percentSign(percent(infoData.high))}
                {percent(infoData.high)}%
              </span>
            )}
          </div>
          <div style={{ color: theme === 'dark' ? '#d1d4dc' : '#666' }}>
            저가:{' '}
            <span style={{ color: theme === 'dark' ? '#d1d4dc' : '#222' }}>
              {formatPrice(infoData.low)}
            </span>
            {percent(infoData.low) && (
              <span
                style={{
                  color: percentColor(percent(infoData.low)),
                  marginLeft: 4,
                }}
              >
                {percentSign(percent(infoData.low))}
                {percent(infoData.low)}%
              </span>
            )}
          </div>
          <div style={{ color: theme === 'dark' ? '#d1d4dc' : '#666' }}>
            종가:{' '}
            <span style={{ color: theme === 'dark' ? '#d1d4dc' : '#222' }}>
              {formatPrice(infoData.close)}
            </span>
            {percent(infoData.close) && (
              <span
                style={{
                  color: percentColor(percent(infoData.close)),
                  marginLeft: 4,
                }}
              >
                {percentSign(percent(infoData.close))}
                {percent(infoData.close)}%
              </span>
            )}
          </div>
        </div>
        <div style={{ marginBottom: 4, display: 'flex', gap: 12 }}>
          {maColors.map((ma: any) => (
            <span
              key={ma.label}
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <span
                style={{
                  width: 16,
                  height: 3,
                  background: ma.color,
                  display: 'inline-block',
                  borderRadius: 2,
                }}
              ></span>
              {ma.label}
            </span>
          ))}
        </div>
        <div>거래량: {volume?.toLocaleString()}</div>
      </div>
    );
  };

  // 기존 차트 컨테이너에 드래그 관련 이벤트 제거
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '4px 8px 0 0',
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => chartRef.current?.timeScale().fitContent()}
          style={{
            fontSize: 12,
            padding: '2px 8px',
            borderRadius: 4,
            border: '1px solid #888',
            background: theme === 'dark' ? '#222' : '#eee',
            color: theme === 'dark' ? '#fff' : '#222',
            cursor: 'pointer',
          }}
        >
          전체 보기
        </button>
      </div>
      <div
        ref={chartContainerRef}
        className="tv-chart-container"
        style={{
          width: '100%',
          flex: 1,
          minHeight: 0,
          boxSizing: 'border-box',
          position: 'relative',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={() =>
          setDragZoom({
            start: null,
            end: null,
            xStart: null,
            xEnd: null,
            active: false,
            valid: false,
          })
        }
        onWheel={handleWheel}
      >
        {/* 차트 내부 좌측 상단에 정보 UI 오버레이 */}
        {showOHLCInfo && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: 'auto',
              minWidth: 180,
              zIndex: 10,
              pointerEvents: 'none',
            }}
          >
            {renderTopInfo()}
          </div>
        )}
        {/* 마커 호버 툴팁 */}
        {hoveredMarker && markerTooltipPosition && (
          <div
            style={{
              position: 'absolute',
              left: markerTooltipPosition.x + 10,
              top: markerTooltipPosition.y - 10,
              background:
                theme === 'dark'
                  ? 'rgba(24, 28, 36, 0.95)'
                  : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme === 'dark' ? '#444' : '#dee2e6'}`,
              borderRadius: '8px',
              padding: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              zIndex: 20,
              pointerEvents: 'none',
              minWidth: '200px',
              color: theme === 'dark' ? '#fff' : '#222',
            }}
          >
            <div
              style={{
                fontWeight: 'bold',
                marginBottom: '8px',
                color: hoveredMarker.type === 'buy' ? '#26a69a' : '#ef5350',
                fontSize: '14px',
              }}
            >
              {hoveredMarker.type === 'buy' ? '▲ 매수' : '▼ 매도'}
            </div>
            <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '4px',
                }}
              >
                <span style={{ color: theme === 'dark' ? '#999' : '#666' }}>
                  가격:
                </span>
                <span style={{ fontWeight: 'bold' }}>
                  ₩{Math.floor(hoveredMarker.price).toLocaleString()}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '4px',
                }}
              >
                <span style={{ color: theme === 'dark' ? '#999' : '#666' }}>
                  수량:
                </span>
                <span style={{ fontWeight: 'bold' }}>
                  {hoveredMarker.quantity}주
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '4px',
                }}
              >
                <span style={{ color: theme === 'dark' ? '#999' : '#666' }}>
                  총액:
                </span>
                <span style={{ fontWeight: 'bold' }}>
                  ₩
                  {(
                    hoveredMarker.price * hoveredMarker.quantity
                  ).toLocaleString()}
                </span>
              </div>
              {hoveredMarker.profit !== undefined && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: `1px solid ${
                      theme === 'dark' ? '#444' : '#dee2e6'
                    }`,
                  }}
                >
                  <span style={{ color: theme === 'dark' ? '#999' : '#666' }}>
                    수익률:
                  </span>
                  <span
                    style={{
                      fontWeight: 'bold',
                      color: hoveredMarker.profit > 0 ? '#26a69a' : '#ef5350',
                    }}
                  >
                    {hoveredMarker.profit > 0 ? '+' : ''}
                    {hoveredMarker.profit.toFixed(2)}%
                  </span>
                </div>
              )}
              {hoveredMarker.note && (
                <div
                  style={{
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: `1px solid ${
                      theme === 'dark' ? '#444' : '#dee2e6'
                    }`,
                    fontSize: '11px',
                    color: theme === 'dark' ? '#aaa' : '#777',
                    fontStyle: 'italic',
                  }}
                >
                  {hoveredMarker.note}
                </div>
              )}
            </div>
          </div>
        )}
        {/* 드래그 영역 및 포인트 시각화 */}
        {(() => {
          const visual = getDragZoomVisual();
          if (!visual) return null;
          return (
            <>
              <div
                style={{
                  position: 'absolute',
                  left: visual.left,
                  top: 0,
                  width: visual.width,
                  height: '100%',
                  background: visual.isZoomIn
                    ? theme === 'dark'
                      ? 'rgba(0,255,136,0.18)'
                      : 'rgba(255,71,87,0.18)'
                    : theme === 'dark'
                    ? 'rgba(255,180,0,0.18)'
                    : 'rgba(23,99,182,0.18)',
                  border: `2px dashed ${
                    visual.isZoomIn
                      ? theme === 'dark'
                        ? '#00ff88'
                        : '#ff4757'
                      : theme === 'dark'
                      ? '#ffa502'
                      : '#1763b6'
                  }`,
                  zIndex: 10,
                  pointerEvents: 'none',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: visual.startX,
                  top: 0,
                  width: 2,
                  height: '100%',
                  background: theme === 'dark' ? '#00d4ff' : '#ff6b35',
                  zIndex: 11,
                  pointerEvents: 'none',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: visual.endX,
                  top: 0,
                  width: 2,
                  height: '100%',
                  background: theme === 'dark' ? '#ffa502' : '#1763b6',
                  zIndex: 11,
                  pointerEvents: 'none',
                }}
              />
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default StockChart;
